<template>
  <div class="show_all_content_wrapper">
    <main>
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.campaignName')"
                v-model.trim="filterOptions.campaign_name"
              />

              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.fromDate')"
                v-model.trim="filterOptions.from"
              />

              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.toDate')"
                v-model.trim="filterOptions.to"
              />
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.marketingCampaignsReports") }}</h5>
          <span class="total_count" v-if="paginations.total">
            ({{ paginations.total }} {{ $t("PLACEHOLDERS.campaignsCount") }})
          </span>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper d-flex align-items-center gap-2">
          <span
            class="ga4_status_badge"
            :class="ga4Status === 'ok' ? 'success' : 'warning'"
          >
            GA4: {{ formatGa4Status(ga4Status) }}
          </span>

          <router-link
            v-if="$can('campaign_reports create', 'campaign_reports')"
            to="/marketing-campaigns/create"
          >
            {{ $t("PLACEHOLDERS.addMarketingCampaign") }}
          </router-link>
        </div>
      </div>

      <div class="statics mt-3 mb-4">
        <div class="row">
          <div class="col-lg-3 col-md-6 col-12">
            <div class="box">
              <div class="icon">
                <i class="fas fa-users"></i>
              </div>
              <div class="info_box">
                <span>{{ $t("PLACEHOLDERS.totalSessions") }}</span>
                <p class="number_box">{{ formatNumber(totals.total_sessions) }}</p>
              </div>
            </div>
          </div>

          <div class="col-lg-3 col-md-6 col-12">
            <div class="box">
              <div class="icon">
                <i class="fas fa-bullseye"></i>
              </div>
              <div class="info_box">
                <span>{{ $t("PLACEHOLDERS.totalConversions") }}</span>
                <p class="number_box">
                  {{ formatNumber(totals.total_conversions) }}
                </p>
              </div>
            </div>
          </div>

          <div class="col-lg-3 col-md-6 col-12">
            <div class="box">
              <div class="icon">
                <i class="fas fa-percentage"></i>
              </div>
              <div class="info_box">
                <span>{{ $t("PLACEHOLDERS.avgConversionRate") }}</span>
                <p class="number_box">
                  {{ formatPercentage(totals.avg_conversion_rate) }}
                </p>
              </div>
            </div>
          </div>

          <div class="col-lg-3 col-md-6 col-12">
            <div class="box">
              <div class="icon">
                <i class="fas fa-money-bill-wave"></i>
              </div>
              <div class="info_box">
                <span>{{ $t("PLACEHOLDERS.totalCost") }}</span>
                <p class="number_box">{{ formatCost(totals.total_cost) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="chart_card mb-4" v-if="daily.labels.length">
        <h5 class="chart_title mb-3">{{ $t("PLACEHOLDERS.dailySessions") }}</h5>
        <apexchart
          type="line"
          height="320"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
      <div class="chart_empty mb-4" v-else-if="!loading">
        <p>{{ $t("PLACEHOLDERS.noDailySessionsData") }}</p>
      </div>

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>

        <template v-slot:[`item.campaign_id`]="{ item, index }">
          <div class="table_image_wrapper">
            <p>
              {{
                item.campaign_id ||
                (paginations.current_page - 1) * paginations.items_per_page +
                  index +
                  1
              }}
            </p>
          </div>
        </template>

        <template v-slot:[`item.sessions`]="{ item }">
          <span>{{ formatNullableNumber(item.sessions) }}</span>
        </template>

        <template v-slot:[`item.conversions`]="{ item }">
          <span>{{ formatNullableNumber(item.conversions) }}</span>
        </template>

        <template v-slot:[`item.conversion_rate`]="{ item }">
          <span>{{ formatNullablePercentage(item.conversion_rate) }}</span>
        </template>

        <template v-slot:[`item.bounce_rate`]="{ item }">
          <span>{{ formatNullablePercentage(item.bounce_rate) }}</span>
        </template>

        <template v-slot:[`item.cost`]="{ item }">
          <span>{{ formatNullableCost(item.cost) }}</span>
        </template>

        <template v-slot:[`item.roi`]="{ item }">
          <span>{{ formatNullablePercentage(item.roi) }}</span>
        </template>

        <template v-slot:[`item.ga4_status`]="{ item }">
          <span
            class="ga4_status_badge table_badge"
            :class="item.ga4_status === 'ok' ? 'success' : 'warning'"
          >
            {{ formatGa4Status(item.ga4_status) }}
          </span>
        </template>
      </v-data-table>
    </main>

    <template v-if="paginations.last_page > 1">
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllMarketingCampaigns",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    chartOptions() {
      return {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        colors: ["#008274"],
        markers: {
          size: 5,
        },
        xaxis: {
          categories: this.daily.labels,
        },
        yaxis: {
          labels: {
            formatter: (value) => Math.round(value),
          },
        },
        tooltip: {
          y: {
            formatter: (value) => this.formatNumber(value),
          },
        },
      };
    },

    chartSeries() {
      return [
        {
          name: this.$t("PLACEHOLDERS.sessions"),
          data: this.daily.sessions,
        },
      ];
    },
  },

  data() {
    return {
      loading: false,
      isWaitingRequest: false,
      filterFormIsActive: false,
      filterOptions: {
        campaign_name: null,
        from: null,
        to: null,
      },
      totals: {
        total_sessions: 0,
        total_conversions: 0,
        avg_conversion_rate: 0,
        total_cost: 0,
      },
      daily: {
        labels: [],
        sessions: [],
      },
      ga4Status: null,
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "campaign_id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.campaignName"),
          value: "campaign_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.source"),
          value: "source",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.sessions"),
          value: "sessions",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.conversions"),
          value: "conversions",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.conversionRate"),
          value: "conversion_rate",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.bounceRate"),
          value: "bounce_rate",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.cost"),
          value: "cost",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.roi"),
          value: "roi",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.ga4Status"),
          value: "ga4_status",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      paginations: {
        current_page: 1,
        last_page: 1,
        total: 0,
        items_per_page: 15,
      },
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    formatNumber(value) {
      if (value === null || value === undefined) {
        return 0;
      }
      return Number(value).toLocaleString();
    },

    formatPercentage(value) {
      if (value === null || value === undefined) {
        return "0%";
      }
      return `${Number(value).toFixed(2)}%`;
    },

    formatCost(value) {
      if (value === null || value === undefined) {
        return `0 ${this.$t("PLACEHOLDERS.riyal")}`;
      }
      return `${Number(value).toLocaleString()} ${this.$t("PLACEHOLDERS.riyal")}`;
    },

    formatNullableNumber(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return Number(value).toLocaleString();
    },

    formatNullablePercentage(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return `${Number(value).toFixed(2)}%`;
    },

    formatNullableCost(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return `${Number(value).toLocaleString()} ${this.$t("PLACEHOLDERS.riyal")}`;
    },

    formatGa4Status(status) {
      if (!status) {
        return this.$t("TABLES.noData");
      }
      const key = `PLACEHOLDERS.ga4Status_${status}`;
      const translated = this.$t(key);
      return translated !== key ? translated : status;
    },

    async submitFilterForm() {
      if (
        this.filterOptions.from &&
        this.filterOptions.to &&
        this.filterOptions.from > this.filterOptions.to
      ) {
        this.$message.error(this.$t("VALIDATION.dateRangeInvalid"));
        return;
      }

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/marketing-campaigns/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.campaign_name = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/marketing-campaigns/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: "marketing-campaigns/reports",
          params: {
            page: this.paginations.current_page,
            campaign_name: this.filterOptions.campaign_name || undefined,
            from: this.filterOptions.from || undefined,
            to: this.filterOptions.to || undefined,
          },
        });

        const { data: reportData = {}, meta = {} } = res.data || {};

        this.tableRows = Array.isArray(reportData.data) ? reportData.data : [];
        this.totals = {
          total_sessions: reportData.totals?.total_sessions ?? 0,
          total_conversions: reportData.totals?.total_conversions ?? 0,
          avg_conversion_rate: reportData.totals?.avg_conversion_rate ?? 0,
          total_cost: reportData.totals?.total_cost ?? 0,
        };
        this.daily = {
          labels: reportData.daily?.labels || [],
          sessions: reportData.daily?.sessions || [],
        };
        this.ga4Status = reportData.ga4_status || null;

        this.paginations.current_page = meta.current_page || 1;
        this.paginations.last_page = meta.last_page || 1;
        this.paginations.total = meta.total ?? this.tableRows.length;
        this.paginations.items_per_page =
          meta.per_page || this.tableRows.length || 15;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$message.error(
          error?.response?.data?.message ||
            this.$t("MESSAGES.somethingWentWrong")
        );
      }
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
  },
};
</script>

<style lang="scss" scoped>
.show_all_content_wrapper {
  .statics {
    .box {
      background: #008274;
      padding: 20px;
      margin-bottom: 10px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 15px;
      min-height: 110px;

      .icon {
        height: 50px;
        min-width: 50px;
        width: 50px;
        border-radius: 50%;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 20px;
          color: #008274;
        }
      }

      .info_box {
        span {
          font-size: 14px;
          color: #fff;
        }

        p.number_box {
          font-size: 24px;
          color: #fff;
          font-weight: bold;
          margin: 0;
        }
      }
    }
  }

  .chart_card {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .chart_empty {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 30px;
    text-align: center;
    color: #6c757d;
  }

  .total_count {
    font-size: 14px;
    color: #6c757d;
    margin-inline-start: 8px;
  }

  .ga4_status_badge {
    font-size: 13px;
    padding: 4px 10px;
    border-radius: 20px;
    font-weight: 600;

    &.success {
      background: #e8f5e9;
      color: #2e7d32;
    }

    &.warning {
      background: #fff3e0;
      color: #ef6c00;
    }

    &.table_badge {
      display: inline-block;
      white-space: nowrap;
    }
  }
}
</style>
